import { useState, useRef, useCallback } from "react";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Radar } from "react-chartjs-2";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Colors for up to 3 sections — in order of first appearance
const SECTION_COLORS = ["#ED1C2E", "#263842", "#2E7D32"];

// Build { sectionName → color } from rawData (ordered by section display_order)
const buildColorMap = (rawData) => {
  const seen = [];
  (rawData || []).forEach((r) => {
    if (r.section_name && !seen.includes(r.section_name))
      seen.push(r.section_name);
  });
  const map = {};
  seen.forEach((name, i) => {
    map[name] = SECTION_COLORS[i] || "#9ca3af";
  });
  return { map, sections: seen };
};

const MAX_LABEL_LENGTH = 25;
const truncate = (str) =>
  str.length > MAX_LABEL_LENGTH
    ? str.slice(0, MAX_LABEL_LENGTH - 1) + "…"
    : str;

const SpiderChart = ({ chartData, rawData, firstChartData }) => {
  const chartRef = useRef(null);
  const [tooltip, setTooltip] = useState(null); // { text, x, y }

  const handleMouseMove = useCallback(
    (e) => {
      const chart = chartRef.current;
      if (!chart) return;

      const scale = chart.scales?.r;
      if (!scale) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const labels = chartData.labels || [];
      for (let i = 0; i < labels.length; i++) {
        // Only show tooltip for truncated labels
        if (labels[i].length <= MAX_LABEL_LENGTH) continue;

        // _pointLabelItems is not populated when pointLabels.display is false (mobile)
        if (!scale._pointLabelItems?.[i]) continue;

        const pos = scale.getPointLabelPosition(i);
        if (!pos) continue;

        // Check if mouse is within the label bounding box (with some padding)
        const pad = 6;
        if (
          mouseX >= pos.left - pad &&
          mouseX <= pos.right + pad &&
          mouseY >= pos.top - pad &&
          mouseY <= pos.bottom + pad
        ) {
          setTooltip({ text: labels[i], x: e.clientX, y: e.clientY });
          return;
        }
      }
      setTooltip(null);
    },
    [chartData.labels]
  );

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  if (!chartData?.labels?.length) {
    return <p>No data available</p>;
  }

  const isMobile = typeof window !== "undefined" && window.innerWidth < 600;
  const { map: colorMap, sections: sectionNames } = buildColorMap(rawData);

  // Each point's color comes from its own section_name in rawData
  const pointColors = (rawData || []).map(
    (r) => colorMap[r.section_name] || "#9ca3af"
  );

  const datasets = [
    {
      label: "Current Score",
      data: chartData.values,
      backgroundColor: "rgba(38, 56, 66, 0.10)",
      borderColor: "rgba(38, 56, 66, 0.35)",
      borderWidth: 1.5,
      pointBackgroundColor: pointColors,
      pointBorderColor: pointColors,
      pointBorderWidth: 1.5,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointHoverBackgroundColor: pointColors,
      pointHoverBorderColor: "#fff",
      pointHoverBorderWidth: 2,
    },
  ];

  if (firstChartData && firstChartData.values) {
    datasets.push({
      label: "Previous Score",
      data: firstChartData.values,
      backgroundColor: "rgba(237, 28, 46, 0.05)",
      borderColor: "rgba(237, 28, 46, 0.4)",
      borderWidth: 1.5,
      borderDash: [5, 5],
      pointBackgroundColor: "#ED1C2E",
      pointBorderColor: "#ED1C2E",
      pointBorderWidth: 1.5,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: "#ED1C2E",
      pointHoverBorderColor: "#fff",
      pointHoverBorderWidth: 2,
    });
  }

  const data = {
    labels: chartData.labels,
    datasets,
  };

  const options = {
    layout: { padding: isMobile ? 0 : 10 },
    scales: {
      r: {
        min: 0,
        max: 5,
        ticks: {
          stepSize: 1,
          font: { size: 9 },
          color: "#9ca3af",
          backdropColor: "transparent",
        },
        grid: { color: "rgba(0,0,0,0.07)" },
        angleLines: { color: "rgba(0,0,0,0.07)" },
        pointLabels: {
          display: !isMobile,
          font: { size: 10, weight: "600" },
          color: "#263842",
          callback: (label) => truncate(label),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (ctx.raw == null) return null;
            return ` Score: ${
              Number(ctx.raw) % 1 === 0 ? ctx.raw : Number(ctx.raw).toFixed(1)
            } / 5`;
          },
        },
      },
    },
  };

  return (
    <div
      style={{ width: "100%", boxSizing: "border-box", position: "relative" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <Radar ref={chartRef} data={data} options={options} />

      {/* Full label tooltip on hover */}
      {tooltip && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x + 12,
            top: tooltip.y - 36,
            background: "#263842",
            color: "#fff",
            padding: "5px 10px",
            borderRadius: 5,
            fontSize: 12,
            fontFamily: "Gotham, sans-serif",
            fontWeight: 600,
            pointerEvents: "none",
            zIndex: 9999,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Section colour legend */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          marginTop: 10,
          flexWrap: "wrap",
          width: "100%",
          boxSizing: "border-box",
          paddingBottom: 4,
        }}
      >
        {sectionNames.map((name, idx) => (
          <div
            key={name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontFamily: "Gotham, sans-serif",
              fontSize: 12,
            }}
          >
            <span
              style={{
                width: 11,
                height: 11,
                borderRadius: "50%",
                backgroundColor: SECTION_COLORS[idx] || "#9ca3af",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontWeight: 600,
                color: "#263842",
                textTransform: "uppercase",
              }}
            >
              {name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpiderChart;
